import { IObjectSignature } from 'types';
import { IState } from '../services/root-reducer';
import { WithOptionalState } from './with-optional-state';
import { getOperationData } from './index';

export const getOmjsViewInstance = WithOptionalState(
    (state: IState) => state.omjs.omjsViewInstance
);

export const getOmjsProjectInstance = WithOptionalState(
    (state: IState) => state.omjs.omjsViewInstance.getProject()
);

export const getCachedPubContainers = WithOptionalState(
    (state: IState): omjs.ProjectLayer[] => {
        let project = getOmjsProjectInstance(state);
        return project.getCachedPubContainers();
    }
);

export const getCachedGpPubContainers = WithOptionalState(
    (state: IState): omjs.ProjectLayer[] => {
        const gpIds = getOperationData(state, 'GEN_PLANES')?.allIds || [];
        let project = getOmjsProjectInstance(state);
        const pubContainers = project.getCachedPubContainers();

        return pubContainers.filter((c) => gpIds.includes(c.get('id')));
    }
);

export const getCachedFullContainers = WithOptionalState(
    (state: IState): omjs.ProjectLayer[] => {
        let project = getOmjsProjectInstance(state);
        return project.getCachedContainers();
    }
);

export const getLayerObject = WithOptionalState(
    (state: IState, layer_code: string, object_id: number) => {
        let containers = getCachedFullContainers(state);
        let [ container ] = containers.filter(c => c.get('code') === layer_code);

        if (!container) {
            return null;
        }

        let object = container.objects.filter(o => o.id === object_id).at(0);

        if (object) {
            return object;
        } else {
            return null;
        }
    }
);

export const getOmjsContainer = WithOptionalState(
    (state: IState, id_or_code: string | number, full = false): omjs.ProjectLayer | undefined => {
        let containers = [];
        let attr = typeof id_or_code === 'number' ? 'id' : 'code';

        if (full) {
            containers = getCachedFullContainers(state);
        } else {
            containers = getCachedPubContainers(state);
        }

        return containers.find(item => item.get(attr) === id_or_code);
    }
);

export const getOmjsChildContainers = WithOptionalState(
    (state: IState, parentId: number, full = false, deep = false) => {
        const containers = full ? getCachedFullContainers(state) : getCachedPubContainers(state);
        let childs = containers.filter(cont => cont.get('parent_id') === parentId);
        if (deep) {
            for (let child of childs) {
                if (child.get('type') === 'folder') {
                    childs = [ ...childs, ...getOmjsChildContainers(state, child.get('id'), deep, full) ];
                }
            }
        }
        return childs;
    }
);

export const getOmjsViewLayer = WithOptionalState(
    (state: IState, contCode: string) => {
        const view = getOmjsViewInstance(state);
        const targetCont = getOmjsContainer(state, contCode, true);

        let _contCode = contCode;
        if (targetCont.get('type') === 'folder') {
            const childs = getOmjsChildContainers(state, targetCont.get('id'), true, true)
                .filter(child => child.get('type') !== 'folder');
            _contCode = childs[0].get('code');
        }

        for (let layer of view.getLayers().getArray()) {
            const containers = layer.getProjectLayers();
            if (containers) {
                const container = containers.getArray().find(cont => cont.get('code') === _contCode);
                if (container) {
                    return layer;
                }
            }
        }
        return undefined;
    }
);

export const getLayerIcon = WithOptionalState(
    (state: IState, object_signature: IObjectSignature) => {
        let {
            layer_code,
            object_id
        } = object_signature;
        let project = getOmjsProjectInstance(state);
        let layer = getOmjsContainer(state, layer_code);
        let object = getLayerObject(state, layer_code, object_id);
        let path = `//${project.apiUrlLocation.hostname}`;
        let styleMode = parseInt(layer.get('style_mode'));

        if (__PROXY_URL__) {
            path = __PROXY_URL__;
        }
        const layerIcon = layer.get('icon');
        switch (styleMode) {
            case 1: {
                return path + (layer.get('vectorSettings')?.stylesPoint.icon || layerIcon);
            }

            case 3: {
                let vs = layer.get('visualSettings');
                let iconMap = {};
                vs.classesSettings.classes.forEach((item) => {
                    iconMap[item.value] = item.icon;
                });

                return path + (iconMap[object?.get(vs.columnCode)] || layerIcon);
            }

            default: {
                return path + layerIcon;
            }
        }
    }
);

export const getOmjsLayerConfig = WithOptionalState(
    (state: IState, id_or_code: string | number): omjs.ILayerConfig | false => {
        let view = getOmjsViewInstance(state);
        let container = getOmjsContainer(state, id_or_code);

        if (!container) {
            return false;
        }

        // return view.options.publication.layers_config.find(item => item.containerId === container.get('id'));
        return state.app.config.publication.layers_config.find(item => item.containerId === container.get('id'));
    }
);

/**
 * Получает родителя с `parent = 0` для контейнера с `container_code`
 */
export const getTopContainer = WithOptionalState(
    (state: IState, container_code: string) => {
        let container = getOmjsContainer(state, container_code);
        return getOmjsContainer(state, container.get('parent_id'));

        // while (container) {
        //     container = getOmjsContainer(state, container.get('parent_id'));
        //     if (container) {
        //         top_container = container;
        //     }
        // }

        // return top_container;
    }
);

export const getOmsContainerObjects = (state: IState, key) => {
    const view = state.omjs.omjsViewInstance;
};

export const getCustomWidgetSettings = WithOptionalState(
    (state: IState, custom_widget_key: string) => {
        let view = getOmjsViewInstance(state);
        let { options } = view;
        let { customWidgets } = options.publication.settings;
        return customWidgets[custom_widget_key] || null;
    }
);

export const getMapParams = (state) => {
    let view = getOmjsViewInstance(state);
    const mapView = view.getMap().getView();
    const pubContainers = getCachedPubContainers(state);

    let center = omjs.ol.proj.toLonLat(mapView.getCenter());
    center = [ center[0].toFixed(6), center[1].toFixed(6) ].join(',');
    const zoom = Math.round(omjs.Utils.getZoomFromResolution(mapView.getResolution()));

    let activeRasterLayers = [];
    const activeVectorLayers = [];
    const activeGpRasterLayers = [];
    const lTypes = [ 'layer', 'virtual', 'raster' ];

    pubContainers.forEach((c) => {
        if (!!~lTypes.indexOf(c.get('type')) && c.get('selected')) {
            if (c.get('render_type') === 1) {
                activeVectorLayers.push(c.get('id'));
            } else {
                activeRasterLayers.push(c.get('id'));
            }
        }
    });

    const gpOpData = getOperationData(state, 'GEN_PLANES');

    if (gpOpData) {
        const gpIds = gpOpData.allIds;
        activeRasterLayers = activeRasterLayers.filter((id) => {
            if (gpIds.includes(id)) {
                activeGpRasterLayers.push(id);
                return false;
            }
            return true;
        });
    }

    return {
        center, zoom, activeRasterLayers, activeVectorLayers, activeGpRasterLayers
    };
};

export const getOmjsUrlParams = (state) => {
    const mapParams = getMapParams(state);

    return {
        center: mapParams.center,
        zoom: mapParams.zoom,
        layers: [ ...mapParams.activeRasterLayers, ...mapParams.activeVectorLayers ].join(',')
    };
};
